import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Star, Check, ShieldCheck } from 'lucide-react';
import { trackAddToCart } from '../../utils/metaPixel';
import { Price } from '../Price';
import { useCurrency } from '../../contexts/CurrencyContext';
const featuredReviews = [{
  text: 'I used the 15-minute rule yesterday and we actually made it to dinner without tears. I feel like I have my brain back.',
  name: 'Sarah M.'
}, {
  text: 'We went from daily meltdowns to maybe one a week. And when they happen, I know exactly what to do. Game changer.',
  name: 'Rachel P.'
}, {
  text: 'I stopped dreading pickup. That sentence alone would have been worth 10x the price. Thank you.',
  name: 'Katie H.'
}];
export function HeroSection() {
  const [showModal, setShowModal] = useState(false);
  const [currentReview, setCurrentReview] = useState(0);
  const {
    convertPrice,
    currencyCode
  } = useCurrency();
  const price = 6.39;
  const originalPrice = 27;
  const handleAddToCart = () => {
    const convertedValue = Number(convertPrice(price).toFixed(2));
    trackAddToCart(convertedValue, currencyCode);
  };
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      const interval = setInterval(() => {
        setCurrentReview(prev => (prev + 1) % featuredReviews.length);
      }, 4000);
      return () => {
        clearInterval(interval);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [showModal]);
  return <>
      <section className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 pb-16 px-4 md:px-6" data-id="element-65">
        {/* Background Blobs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sage-light/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 z-0" data-id="element-66" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-dusty-blue/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 z-0" data-id="element-67" />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative z-10" data-id="element-68">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="flex flex-col text-center lg:text-left" data-id="element-69">
            <span className="inline-block py-1 px-3 rounded-full bg-sage-light/30 text-sage-dark text-sm font-bold tracking-wide mb-6 w-fit mx-auto lg:mx-0" data-id="element-70">
              FOR MOMS WHO DREAD 4PM
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-sage-dark" data-id="element-71">
              After-School Evenings{' '}
              <span className="italic text-terracotta" data-id="element-72">Without</span> the
              Meltdowns
            </h1>

            <p className="text-xl md:text-2xl text-sage-dark/80 mb-8 font-medium" data-id="element-73">
              Why 4PM Feels So Hard for Your Child — and the 30-Minute Reset
              That Helps
            </p>

            <p className="text-lg text-sage-dark/70 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-74">
              A calm, doable reset for the hardest time of day—so you can stop
              dreading 4PM and start feeling capable again.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-75">
              <button onClick={() => setShowModal(true)} className="inline-flex items-center justify-center px-8 py-4 bg-terracotta text-white font-bold rounded-full shadow-lg hover:bg-terracotta-hover hover:scale-105 transition-all duration-300 text-lg group" data-id="element-76">
                Try this tonight
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-77" />
              </button>
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="relative" data-id="element-78">
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50" data-id="element-79">
              <img src="https://cdn.magicpatterns.com/uploads/s979R5jC6LpgkZkVgSxAfW/sales_page_image_3.png" alt="Tantrum-Free After-School Evenings Guide on Phone" className="w-full h-auto object-cover" data-id="element-80" />
            </div>

            {/* Decorative elements behind image */}
            <svg className="absolute -bottom-10 -right-10 w-32 h-32 text-sage-light opacity-50 z-0" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" data-id="element-81">
              <path fill="currentColor" d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-5.3C93.5,8.6,82.2,21.5,70.6,32.3C59,43.1,47.1,51.8,34.8,59.3C22.5,66.8,9.8,73.1,-1.8,76.2C-13.4,79.3,-23.9,79.2,-34.2,74.5C-44.5,69.8,-54.6,60.5,-63.4,49.8C-72.2,39.1,-79.7,27,-82.4,13.9C-85.1,0.8,-83,-13.3,-75.7,-25.1C-68.4,-36.9,-55.9,-46.4,-43.3,-54.3C-30.7,-62.2,-18,-68.5,-4.4,-60.9L9.2,-84.6" transform="translate(100 100)" data-id="element-82" />
            </svg>
          </motion.div>
        </div>
      </section>

      {/* Purchase Modal */}
      <AnimatePresence data-id="element-83">
        {showModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-id="element-84">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-sage-dark/40 backdrop-blur-sm" data-id="element-85" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-cream-100 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border-2 border-sage-light/30" data-id="element-86">
              {/* Close button */}
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center text-sage-dark/60 hover:bg-terracotta/10 hover:text-terracotta transition-colors" data-id="element-87">
                <X size={18} data-id="element-88" />
              </button>

              <div className="p-8 md:p-10" data-id="element-89">
                {/* Header */}
                <div className="text-center mb-6" data-id="element-90">
                  <h3 className="text-2xl md:text-3xl font-bold text-sage-dark font-serif mb-2" data-id="element-91">
                    After-School Evenings Without the Meltdowns
                  </h3>
                  <p className="text-sage-dark/60 font-medium" data-id="element-92">
                    The Complete Ebook Guide
                  </p>
                </div>

                {/* Key benefits */}
                <div className="space-y-3 mb-6" data-id="element-93">
                  {['The exact 4PM reset + routines + scripts', 'Quick-start "Try This Tonight" page', 'Decompression checklist'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-94">
                      <div className="w-5 h-5 rounded-full bg-sage-dark text-white flex items-center justify-center flex-shrink-0" data-id="element-95">
                        <Check size={12} data-id="element-96" />
                      </div>
                      <span className="text-sage-dark font-medium" data-id="element-97">{item}</span>
                    </div>)}
                </div>

                {/* Review */}
                <div className="bg-white p-5 rounded-xl border border-sage-light/20 mb-6 min-h-[100px]" data-id="element-98">
                  <div className="flex items-center gap-1 mb-2" data-id="element-99">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" data-id="element-100" />)}
                  </div>
                  <AnimatePresence mode="wait" data-id="element-101">
                    <motion.div key={currentReview} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} data-id="element-102">
                      <p className="text-sage-dark italic text-sm leading-relaxed" data-id="element-103">
                        "{featuredReviews[currentReview].text}"
                      </p>
                      <p className="text-sage-dark/50 text-xs mt-2 font-medium not-italic" data-id="element-104">
                        — {featuredReviews[currentReview].name}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                  <div className="flex items-center justify-center gap-1.5 mt-3" data-id="element-105">
                    {featuredReviews.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === currentReview ? 'bg-terracotta w-3' : 'bg-sage-light/50'}`} data-id="element-106" />)}
                  </div>
                </div>

                {/* Pricing */}
                <div className="text-center mb-6" data-id="element-107">
                  <span className="inline-block bg-terracotta/90 text-white text-sm font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider" data-id="element-108">
                    🔥 76% OFF — Limited Time
                  </span>
                  <div className="flex items-center justify-center gap-3" data-id="element-109">
                    <span className="text-sage-dark/40 line-through text-xl" data-id="element-110">
                      <Price amount={originalPrice} data-id="element-111" />
                    </span>
                    <span className="text-4xl font-bold text-sage-dark" data-id="element-112">
                      <Price amount={price} data-id="element-113" />
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <a href="https://www.gemmamama.com/cart/45458351751307:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="block w-full py-4 bg-terracotta text-white font-bold rounded-xl text-lg hover:bg-terracotta-hover transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-center" data-id="element-114">
                  Get the Guide — <Price amount={price} data-id="element-115" />
                </a>

                {/* Trust */}
                <div className="flex items-center justify-center gap-2 mt-4" data-id="element-116">
                  <ShieldCheck className="w-4 h-4 text-sage-dark/40" data-id="element-117" />
                  <p className="text-xs text-sage-dark/40" data-id="element-118">
                    Secure checkout · Instant digital delivery
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}